import { useState, type CSSProperties } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../auth.js';
import { apiFetch } from '../api-client.js';
import { validateGrantEmail, type Role } from '@death-star/shared';
import { isOnlySuperAdmin, type UserRow } from './users-helpers.js';

const page: CSSProperties = { /* padding now provided by ConfigLayout */ };
const card: CSSProperties = {
  background: 'var(--loop-neutral-0)',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-md)',
  padding: 'var(--loop-space-300)',
  marginBottom: 'var(--loop-space-300)',
};
const label: CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  color: 'var(--loop-neutral-900)',
  marginBottom: 'var(--loop-space-075)',
};
const input: CSSProperties = {
  padding: 'var(--loop-space-100) var(--loop-space-150)',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  background: 'var(--loop-neutral-0)',
  color: 'var(--loop-neutral-1100)',
  minWidth: 280,
};
const errStyle: CSSProperties = {
  color: 'var(--loop-red-700)',
  fontSize: '0.8rem',
  marginTop: 'var(--loop-space-075)',
};
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };
const th: CSSProperties = {
  textAlign: 'left',
  padding: 'var(--loop-space-100)',
  borderBottom: '1px solid var(--loop-neutral-300)',
  color: 'var(--loop-neutral-900)',
  fontWeight: 528,
};
const td: CSSProperties = {
  padding: 'var(--loop-space-100)',
  borderBottom: '1px solid var(--loop-neutral-200)',
  color: 'var(--loop-neutral-1100)',
};

export function Users() {
  const { idToken, email: currentEmail } = useAuth();
  const qc = useQueryClient();
  const usersQ = useQuery<{ users: UserRow[] }>({
    queryKey: ['rbac-users'],
    queryFn: () => apiFetch('/api/rbac/users', idToken!).then((r) => r.json()),
    enabled: !!idToken,
  });

  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<Role>('triager');
  const [formErr, setFormErr] = useState<string | null>(null);

  const putM = useMutation({
    mutationFn: async (args: { email: string; role: Role }) => {
      const res = await apiFetch(`/api/rbac/users/${encodeURIComponent(args.email)}`, idToken!, {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ role: args.role }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rbac-users'] }),
  });

  const delM = useMutation({
    mutationFn: async (email: string) => {
      const res = await apiFetch(`/api/rbac/users/${encodeURIComponent(email)}`, idToken!, {
        method: 'DELETE',
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { message?: string; error?: string };
        throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['rbac-users'] }),
  });

  const onAdd = async () => {
    setFormErr(null);
    const v = validateGrantEmail(newEmail);
    if (!v.ok) { setFormErr(v.message); return; }
    try {
      await putM.mutateAsync({ email: v.email, role: newRole });
      setNewEmail('');
    } catch (e) {
      setFormErr((e as Error).message);
    }
  };

  const users = usersQ.data?.users ?? [];

  return (
    <div style={page}>
      <h2 style={{ marginTop: 0, color: 'var(--loop-neutral-1100)' }}>Access Control</h2>
      <p style={{ color: 'var(--loop-neutral-900)', marginTop: 0 }}>
        viewer = any signed-in Rewind user. triager = process reactions, manage suppressions.
        superAdmin = configuration and access-control.
      </p>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: 'var(--loop-neutral-1100)' }}>Grant access</h3>
        <div style={{ display: 'flex', gap: 'var(--loop-space-200)', alignItems: 'flex-end' }}>
          <div>
            <label style={label} htmlFor="grant-email">Email</label>
            <input
              id="grant-email"
              style={input}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="alice@rewind.io"
              autoComplete="off"
            />
          </div>
          <div>
            <label style={label} htmlFor="grant-role">Role</label>
            <select
              id="grant-role"
              style={input}
              value={newRole}
              onChange={(e) => setNewRole(e.target.value as Role)}
            >
              <option value="triager">triager</option>
              <option value="superAdmin">superAdmin</option>
            </select>
          </div>
          <button className="btn-primary" onClick={() => { void onAdd(); }} disabled={putM.isPending}>
            {putM.isPending ? 'Adding…' : 'Add'}
          </button>
        </div>
        {formErr && <div style={errStyle}>{formErr}</div>}
      </div>

      <div style={card}>
        <h3 style={{ marginTop: 0, color: 'var(--loop-neutral-1100)' }}>Active grants</h3>
        {usersQ.isPending && <div>Loading…</div>}
        {usersQ.isError && <div style={errStyle}>Failed to load users.</div>}
        {usersQ.data && (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>Email</th>
                <th style={th}>Role</th>
                <th style={th}>Granted by</th>
                <th style={th}>Granted at</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const onlySa = isOnlySuperAdmin(users, u.email);
                return (
                  <tr key={u.email}>
                    <td style={td}>{u.email}</td>
                    <td style={td}>
                      <select
                        value={u.role}
                        disabled={onlySa || putM.isPending}
                        title={onlySa ? 'Cannot downgrade the last superAdmin.' : undefined}
                        onChange={(e) => { void putM.mutate({ email: u.email, role: e.target.value as Role }); }}
                        style={{ ...input, minWidth: 140 }}
                      >
                        <option value="triager">triager</option>
                        <option value="superAdmin">superAdmin</option>
                      </select>
                    </td>
                    <td style={td}>{u.granted_by ?? ''}</td>
                    <td style={td}>{u.granted_at ?? ''}</td>
                    <td style={td}>
                      <button
                        className="btn-secondary btn-sm"
                        disabled={onlySa || delM.isPending}
                        title={onlySa ? 'Cannot revoke the last superAdmin.' : undefined}
                        onClick={() => {
                          if (window.confirm(`Revoke ${u.email}?`)) {
                            void delM.mutate(u.email);
                          }
                        }}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                );
              })}
              {users.length === 0 && (
                <tr><td style={td} colSpan={5}>No grants. See README for the bootstrap seed command.</td></tr>
              )}
            </tbody>
          </table>
        )}
        {putM.isError && <div style={errStyle}>{(putM.error as Error).message}</div>}
        {delM.isError && <div style={errStyle}>{(delM.error as Error).message}</div>}
      </div>

      <div style={{ ...card, opacity: 0.7 }}>
        <small style={{ color: 'var(--loop-neutral-700)' }}>
          You ({currentEmail}) are signed in. Role changes propagate within ~60 seconds.
        </small>
      </div>
    </div>
  );
}
